import { useMemo, useState, type CSSProperties, type ReactNode } from "react";

import {
  AGE_FILTERS,
  AR_AGING_BUCKETS,
  AR_AGING_STATUSES,
  accountsFromRows,
  ageFilter,
  bucketAccounts,
  bucketPageSummary,
  criticalSummary,
  normalizeSegmentRule,
  priority,
  riskList,
  shareOfTotal,
  sortAccounts,
  summarize,
  tableFilter,
  warningList,
} from "../lib/arAging.mjs";
import type {
  ArAccount,
  ArBucketKey,
  ArPriority,
  ArSegmentRule,
  ArSummary,
} from "../lib/arAging.d.mts";
import { useArAgingSnapshot } from "../lib/arAgingData";
import type { ManifestWidget } from "../lib/manifest";
import { GATE_COPY } from "../lib/sanityGate";
import EmptyState from "./states/EmptyState";
import ErrorState from "./states/ErrorState";
import Skeleton from "./states/Skeleton";

/**
 * AR Aging tab (wave 1 rebuild on Zay's AR aging dashboard). One component
 * renders every panel on the tab; `widget.ar_aging.view` picks which. All the
 * arithmetic is in arAging.mjs (pinned by scripts/ar-aging.test.mjs); this
 * file is layout, sorting state and filters only.
 *
 * A panel shows numbers only when the newest AR snapshot has rows. With no
 * snapshot it says it is awaiting source data (GATE_COPY), never a zero.
 */

const BUCKET_COLORS: Record<ArBucketKey, string> = {
  cu: "#1d4ed8",
  d30: "#0369a1",
  d60: "#92400e",
  d90: "#9a3412",
  d120: "#991b1b",
  ov: "#5b21b6",
};

const PRIORITY_STYLE: Record<ArPriority, { bg: string; fg: string }> = {
  Critical: { bg: "var(--flag-red-wash)", fg: "var(--flag-red)" },
  High: { bg: "var(--domain-financial-wash)", fg: "var(--domain-financial)" },
  Medium: { bg: "var(--flag-yellow-wash)", fg: "var(--flag-yellow)" },
  Low: { bg: "var(--flag-green-wash)", fg: "var(--flag-green)" },
};

const money2 = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** Zay's fc(): full cents, or compact ($1.23M / $4.5k / $12). Zero shows a dash. */
function fc(n: number | null | undefined, compact = false): string {
  if (n === null || n === undefined || !Number.isFinite(n) || n === 0) return "-";
  if (compact) {
    const a = Math.abs(n);
    if (a >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
    if (a >= 1e3) return `$${(n / 1e3).toFixed(1)}k`;
    return `$${n.toFixed(0)}`;
  }
  return money2.format(n);
}

function pct(part: number, whole: number): string {
  const share = shareOfTotal(part, whole);
  return share === null ? "-" : `${(share * 100).toFixed(1)}%`;
}

const card: CSSProperties = {
  background: "var(--bg-card)",
  border: "1px solid var(--border-subtle)",
  borderRadius: 10,
  padding: "12px 14px",
};
const label: CSSProperties = {
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: 0.6,
  textTransform: "uppercase",
  color: "var(--text-muted)",
};
const big: CSSProperties = { fontSize: 20, fontWeight: 800, marginTop: 4 };
const small: CSSProperties = { fontSize: 11, color: "var(--text-muted)", marginTop: 2 };
const grid = (min: number): CSSProperties => ({
  display: "grid",
  gap: 10,
  gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))`,
});
const control: CSSProperties = {
  fontSize: 12,
  padding: "4px 8px",
  border: "1px solid var(--border-subtle)",
  borderRadius: 6,
  background: "var(--bg-card)",
};

function PriorityTag({ account }: { account: ArAccount }) {
  const p = priority(account);
  const s = PRIORITY_STYLE[p];
  return (
    <span
      style={{
        fontSize: 10,
        fontWeight: 700,
        padding: "2px 6px",
        borderRadius: 4,
        background: s.bg,
        color: s.fg,
      }}
    >
      {p}
    </span>
  );
}

function segmentLabel(account: ArAccount, rule: ArSegmentRule | null): string {
  if (!rule || !account.segment) return "";
  return account.segment === "residential" ? rule.label : "Commercial";
}

interface Column {
  key: string;
  label: string;
  align?: "right";
  render: (a: ArAccount) => ReactNode;
}

function money(key: string, lbl: string, compact = false, color?: string): Column {
  return {
    key,
    label: lbl,
    align: "right",
    render: (a) => (
      <span style={{ fontFamily: "var(--font-mono)", color }}>{fc(a[key] as number, compact)}</span>
    ),
  };
}

function baseColumns(rule: ArSegmentRule | null): Column[] {
  const cols: Column[] = [{ key: "name", label: "Account", render: (a) => a.name || "-" }];
  if (rule) cols.push({ key: "segment", label: "Type", render: (a) => segmentLabel(a, rule) });
  cols.push({ key: "status", label: "Status", render: (a) => a.status });
  return cols;
}

const daysColumn: Column = {
  key: "days",
  label: "Avg Days",
  align: "right",
  render: (a) => (a.days ? a.days : "-"),
};
const priorityColumn: Column = {
  key: "priorityRank",
  label: "Priority",
  render: (a) => <PriorityTag account={a} />,
};

const PRIORITY_RANK: Record<ArPriority, number> = { Critical: 4, High: 3, Medium: 2, Low: 1 };

/** A sortable account table. Shows at most `limit` rows and says so. */
function AccountTable(props: {
  rows: ArAccount[];
  columns: Column[];
  initialSort: string;
  limit: number;
}) {
  const { rows, columns, initialSort, limit } = props;
  const [sortKey, setSortKey] = useState(initialSort);
  const [dir, setDir] = useState(-1);
  const sorted = useMemo(() => {
    const keyed =
      sortKey === "priorityRank"
        ? rows.map((a) => ({ ...a, priorityRank: PRIORITY_RANK[priority(a)] }))
        : rows;
    return sortAccounts(keyed, sortKey, dir);
  }, [rows, sortKey, dir]);
  const shown = sorted.slice(0, limit);
  const th: CSSProperties = {
    ...label,
    padding: "6px 8px",
    borderBottom: "1px solid var(--border-subtle)",
    cursor: "pointer",
    whiteSpace: "nowrap",
  };
  return (
    <div style={{ overflow: "auto", maxHeight: 460 }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
        <thead>
          <tr>
            {columns.map((c) => (
              <th
                key={c.key}
                style={{ ...th, textAlign: c.align ?? "left" }}
                onClick={() => {
                  if (sortKey === c.key) setDir(-dir);
                  else {
                    setSortKey(c.key);
                    setDir(-1);
                  }
                }}
              >
                {c.label}
                {sortKey === c.key ? (dir === 1 ? " ▲" : " ▼") : ""}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {shown.map((a, i) => (
            <tr key={`${a.id ?? a.name}-${i}`}>
              {columns.map((c) => (
                <td
                  key={c.key}
                  style={{
                    padding: "5px 8px",
                    borderBottom: "1px solid var(--border-subtle)",
                    textAlign: c.align ?? "left",
                    whiteSpace: "nowrap",
                  }}
                >
                  {c.render(a)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ ...small, padding: "6px 8px" }}>
        {sorted.length > limit
          ? `Showing ${limit.toLocaleString()} of ${sorted.length.toLocaleString()} accounts. Use the filters to narrow.`
          : `${sorted.length.toLocaleString()} accounts`}
      </div>
    </div>
  );
}

/** Search, type and status filters shared by the tables. */
function useTableFilters() {
  const [query, setQuery] = useState("");
  const [segment, setSegment] = useState("");
  const [status, setStatus] = useState("");
  return { query, setQuery, segment, setSegment, status, setStatus };
}

function FilterBar(props: {
  rule: ArSegmentRule | null;
  f: ReturnType<typeof useTableFilters>;
  children?: ReactNode;
}) {
  const { rule, f, children } = props;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, margin: "8px 0" }}>
      <input
        style={{ ...control, minWidth: 180 }}
        placeholder="Search accounts"
        value={f.query}
        onChange={(e) => f.setQuery(e.target.value)}
        aria-label="Search accounts"
      />
      {rule ? (
        <select
          style={control}
          value={f.segment}
          onChange={(e) => f.setSegment(e.target.value)}
          aria-label="Account type"
        >
          <option value="">All types</option>
          <option value="commercial">Commercial</option>
          <option value="residential">{rule.label}</option>
        </select>
      ) : null}
      <select
        style={control}
        value={f.status}
        onChange={(e) => f.setStatus(e.target.value)}
        aria-label="Account status"
      >
        <option value="">All statuses</option>
        {AR_AGING_STATUSES.map((s) => (
          <option key={s} value={s}>
            {s}
          </option>
        ))}
      </select>
      {children}
    </div>
  );
}

function BucketTiles(props: {
  accounts: ArAccount[];
  summary: ArSummary;
  rule: ArSegmentRule | null;
}) {
  const { accounts, summary, rule } = props;
  const [active, setActive] = useState<ArBucketKey | null>(null);
  const f = useTableFilters();
  const drill = useMemo(() => (active ? bucketAccounts(accounts, active) : []), [accounts, active]);
  const drillRows = tableFilter(drill, f);
  const def = AR_AGING_BUCKETS.find((b) => b.key === active);
  return (
    <div>
      <div style={grid(130)}>
        {AR_AGING_BUCKETS.map((b) => {
          const amt = summary.totals[b.key];
          const on = active === b.key;
          return (
            <button
              key={b.key}
              type="button"
              onClick={() => setActive(on ? null : b.key)}
              style={{
                ...card,
                textAlign: "left",
                cursor: "pointer",
                borderColor: on ? BUCKET_COLORS[b.key] : "var(--border-subtle)",
                borderWidth: on ? 2 : 1,
              }}
              aria-pressed={on}
            >
              <div style={{ ...label, color: BUCKET_COLORS[b.key] }}>{b.label}</div>
              <div style={big}>{fc(amt, true)}</div>
              <div style={small}>{pct(amt, summary.totals.total)} of AR</div>
              <div style={small}>{summary.bucketCounts[b.key].toLocaleString()} accounts</div>
            </button>
          );
        })}
      </div>
      {def ? (
        <div style={{ ...card, marginTop: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <strong>
              {def.label}: {drill.length.toLocaleString()} accounts
            </strong>
            <button type="button" style={control} onClick={() => setActive(null)}>
              Close
            </button>
          </div>
          <div style={small}>
            In bucket: <strong>{fc(drill.reduce((s, a) => s + (a.bucketValue ?? 0), 0))}</strong>
            {"  ·  "}Total AR owed by these accounts:{" "}
            <strong>{fc(drill.reduce((s, a) => s + a.total, 0))}</strong>
          </div>
          <FilterBar rule={rule} f={f} />
          <AccountTable
            rows={drillRows}
            initialSort="bucketValue"
            limit={200}
            columns={[
              ...baseColumns(rule),
              money("bucketValue", "In bucket", false, BUCKET_COLORS[def.key]),
              money("total", "Total owed", true),
              daysColumn,
              priorityColumn,
            ]}
          />
        </div>
      ) : null}
    </div>
  );
}

function StackBar(props: { totals: ArSummary["totals"] }) {
  const { totals } = props;
  return (
    <div
      style={{
        display: "flex",
        height: 26,
        borderRadius: 6,
        overflow: "hidden",
        gap: 1,
        background: "var(--border-subtle)",
      }}
    >
      {AR_AGING_BUCKETS.map((b) => {
        const share = shareOfTotal(totals[b.key], totals.total) ?? 0;
        const w = Math.max(share * 100, 0);
        return (
          <div
            key={b.key}
            title={`${b.label}: ${fc(totals[b.key])} (${pct(totals[b.key], totals.total)})`}
            style={{
              width: `${w.toFixed(2)}%`,
              background: BUCKET_COLORS[b.key],
              color: "#fff",
              fontSize: 10,
              display: "grid",
              placeItems: "center",
            }}
          >
            {w > 6 ? `${w.toFixed(0)}%` : ""}
          </div>
        );
      })}
    </div>
  );
}

function SegmentPanels(props: { summary: ArSummary; rule: ArSegmentRule }) {
  const { summary, rule } = props;
  if (!summary.segments) return null;
  const panels = [
    { name: "Commercial", seg: summary.segments.commercial },
    { name: rule.label, seg: summary.segments.residential },
  ];
  return (
    <div style={grid(320)}>
      {panels.map(({ name, seg }) => (
        <div key={name} style={card}>
          <div style={label}>{name}</div>
          <div style={big}>{fc(seg.totals.total)}</div>
          <div style={small}>
            {seg.count.toLocaleString()} accounts ·{" "}
            <span style={{ color: "var(--flag-red)" }}>{fc(seg.pastDue, true)} past due</span>
          </div>
          <div style={{ ...grid(80), marginTop: 10 }}>
            {AR_AGING_BUCKETS.map((b) => (
              <div key={b.key}>
                <div style={{ ...label, color: BUCKET_COLORS[b.key] }}>{b.label}</div>
                <div style={{ fontWeight: 700 }}>{fc(seg.totals[b.key], true)}</div>
                <div style={small}>{pct(seg.totals[b.key], seg.totals.total)}</div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 10 }}>
            <StackBar totals={seg.totals} />
          </div>
        </div>
      ))}
    </div>
  );
}

function Distribution({ summary }: { summary: ArSummary }) {
  const t = summary.totals;
  return (
    <div style={card}>
      <div style={label}>Total AR</div>
      <div style={big}>{fc(t.total)}</div>
      <div style={{ marginTop: 10 }}>
        <StackBar totals={t} />
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginTop: 8, fontSize: 11 }}>
        {AR_AGING_BUCKETS.map((b) => (
          <span key={b.key}>
            <span
              style={{
                display: "inline-block",
                width: 9,
                height: 9,
                background: BUCKET_COLORS[b.key],
                marginRight: 4,
                borderRadius: 2,
              }}
            />
            {b.label}: <strong>{fc(t[b.key], true)}</strong>
          </span>
        ))}
      </div>
    </div>
  );
}

function StatusCounts({ summary }: { summary: ArSummary }) {
  const s = summary.status;
  const items = [
    { name: "Active", n: s.active, color: "var(--flag-green)" },
    { name: "Credit Hold", n: s.hold, color: "var(--flag-red)" },
    { name: "Inactive", n: s.inactive, color: "var(--text-muted)" },
  ];
  return (
    <div style={card}>
      <div style={grid(90)}>
        {items.map((i) => (
          <div key={i.name}>
            <div style={label}>{i.name}</div>
            <div style={{ ...big, color: i.color }}>{i.n.toLocaleString()}</div>
          </div>
        ))}
      </div>
      {s.other > 0 ? (
        <div style={{ ...small, marginTop: 8 }}>
          {s.other.toLocaleString()} account{s.other === 1 ? "" : "s"} with another status (for
          example Bad Debt).
        </div>
      ) : null}
    </div>
  );
}

function RiskTop({ accounts }: { accounts: ArAccount[] }) {
  const f = useTableFilters();
  const rows = tableFilter(riskList(accounts), f);
  return (
    <div>
      <FilterBar rule={null} f={f} />
      <AccountTable
        rows={rows}
        initialSort="bucketValue"
        limit={80}
        columns={[
          { key: "name", label: "Account", render: (a) => a.name || "-" },
          { key: "status", label: "Status", render: (a) => a.status },
          money("bucketValue", "91+ Days", true, BUCKET_COLORS.d120),
          money("total", "Total", true),
          money("ov", "Over 120", false, BUCKET_COLORS.ov),
          money("d120", "91-120", false, BUCKET_COLORS.d120),
          daysColumn,
          priorityColumn,
        ]}
      />
    </div>
  );
}

function Warning({ accounts }: { accounts: ArAccount[] }) {
  const [query, setQuery] = useState("");
  const rows = tableFilter(warningList(accounts), { query });
  return (
    <div>
      <input
        style={{ ...control, minWidth: 180, margin: "8px 0" }}
        placeholder="Search accounts"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        aria-label="Search accounts"
      />
      <AccountTable
        rows={rows}
        initialSort="d90"
        limit={80}
        columns={[
          { key: "name", label: "Account", render: (a) => a.name || "-" },
          { key: "status", label: "Status", render: (a) => a.status },
          money("d60", "31-60", false, BUCKET_COLORS.d60),
          money("d90", "61-90", false, BUCKET_COLORS.d90),
          money("total", "Total", true),
          daysColumn,
        ]}
      />
    </div>
  );
}

function Critical(props: { accounts: ArAccount[]; rule: ArSegmentRule | null }) {
  const { accounts, rule } = props;
  const f = useTableFilters();
  const c = criticalSummary(tableFilter(accounts, f));
  return (
    <div>
      <div style={grid(170)}>
        <div style={{ ...card, borderLeft: `3px solid ${BUCKET_COLORS.d120}` }}>
          <div style={{ ...label, color: BUCKET_COLORS.d120 }}>Total At Risk (91+)</div>
          <div style={big}>{fc(c.totalRisk, true)}</div>
          <div style={small}>Total owed: {fc(c.totalOwed, true)}</div>
          <div style={small}>{c.count.toLocaleString()} critical accounts</div>
        </div>
        {rule ? (
          <>
            <div style={card}>
              <div style={label}>Commercial Exposure</div>
              <div style={big}>{fc(c.commercialRisk, true)}</div>
              <div style={small}>{c.commercialCount.toLocaleString()} accounts</div>
            </div>
            <div style={card}>
              <div style={label}>{rule.label} Exposure</div>
              <div style={big}>{fc(c.residentialRisk, true)}</div>
              <div style={small}>{c.residentialCount.toLocaleString()} accounts</div>
            </div>
          </>
        ) : null}
        <div style={card}>
          <div style={label}>On Credit Hold</div>
          <div style={{ ...big, color: "var(--flag-red)" }}>{c.holdCount.toLocaleString()}</div>
          <div style={small}>of {c.count.toLocaleString()} critical accounts</div>
        </div>
      </div>
      <FilterBar rule={rule} f={f} />
      <AccountTable
        rows={c.rows}
        initialSort="bucketValue"
        limit={400}
        columns={[
          ...baseColumns(rule),
          money("bucketValue", "91+ Days", true, BUCKET_COLORS.d120),
          money("total", "Total", true),
          money("ov", "Over 120", false, BUCKET_COLORS.ov),
          money("d120", "91-120", false, BUCKET_COLORS.d120),
          money("d90", "61-90", false, BUCKET_COLORS.d90),
          money("d60", "31-60", false, BUCKET_COLORS.d60),
          daysColumn,
          priorityColumn,
        ]}
      />
    </div>
  );
}

function BucketPages(props: { accounts: ArAccount[]; rule: ArSegmentRule | null }) {
  const { accounts, rule } = props;
  const [key, setKey] = useState<ArBucketKey>("cu");
  const f = useTableFilters();
  const def = AR_AGING_BUCKETS.find((b) => b.key === key) ?? AR_AGING_BUCKETS[0];
  const all = useMemo(() => bucketAccounts(accounts, key), [accounts, key]);
  const rows = tableFilter(all, f);
  const cards = bucketPageSummary(all, rows);
  const cardFor = (
    name: string,
    c: { bucket: number; owed: number; count: number },
    sub: string,
  ) => (
    <div style={card}>
      <div style={label}>{name}</div>
      <div style={big}>{fc(c.bucket, true)}</div>
      <div style={small}>Total owed: {fc(c.owed, true)}</div>
      <div style={small}>{sub}</div>
    </div>
  );
  return (
    <div>
      <div role="tablist" style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 10 }}>
        {AR_AGING_BUCKETS.map((b) => (
          <button
            key={b.key}
            type="button"
            role="tab"
            aria-selected={b.key === key}
            onClick={() => setKey(b.key)}
            style={{
              ...control,
              cursor: "pointer",
              fontWeight: b.key === key ? 700 : 400,
              borderColor: b.key === key ? BUCKET_COLORS[b.key] : "var(--border-subtle)",
              color: BUCKET_COLORS[b.key],
            }}
          >
            {b.label}
          </button>
        ))}
      </div>
      <div style={grid(160)}>
        {cardFor(`${def.label}: All`, cards.all, `${cards.all.count.toLocaleString()} accounts`)}
        {rule
          ? cardFor(
              "Commercial",
              cards.commercial,
              `${cards.commercial.count.toLocaleString()} accts · ${pct(cards.commercial.bucket, cards.all.bucket)}`,
            )
          : null}
        {rule
          ? cardFor(
              rule.label,
              cards.residential,
              `${cards.residential.count.toLocaleString()} accts · ${pct(cards.residential.bucket, cards.all.bucket)}`,
            )
          : null}
        {cardFor(
          "Filtered View",
          cards.filtered,
          `${cards.filtered.count.toLocaleString()} accounts shown`,
        )}
      </div>
      <FilterBar rule={rule} f={f} />
      <AccountTable
        key={key}
        rows={rows}
        initialSort="bucketValue"
        limit={300}
        columns={[
          ...baseColumns(rule),
          money("bucketValue", def.label, false, BUCKET_COLORS[def.key]),
          money("total", "Total owed", true),
          daysColumn,
          priorityColumn,
        ]}
      />
    </div>
  );
}

function AllAccounts(props: { accounts: ArAccount[]; rule: ArSegmentRule | null }) {
  const { accounts, rule } = props;
  const f = useTableFilters();
  const [age, setAge] = useState("any");
  const rows = ageFilter(tableFilter(accounts, f), age);
  return (
    <div>
      <FilterBar rule={rule} f={f}>
        <select
          style={control}
          value={age}
          onChange={(e) => setAge(e.target.value)}
          aria-label="Balance age"
        >
          {AGE_FILTERS.map((a) => (
            <option key={a.key} value={a.key}>
              {a.label}
            </option>
          ))}
        </select>
      </FilterBar>
      <AccountTable
        rows={rows}
        initialSort="total"
        limit={300}
        columns={[
          ...baseColumns(rule),
          ...AR_AGING_BUCKETS.map((b) => money(b.key, b.label, true, BUCKET_COLORS[b.key])),
          money("total", "Total", true),
          daysColumn,
        ]}
      />
    </div>
  );
}

export interface ArAgingWidgetProps {
  widget: ManifestWidget;
}

/** The division ids the page's global division filter injected, if any. */
function selectedDivisions(widget: ManifestWidget): string[] {
  const clause = (widget.filter as Record<string, unknown> | undefined)?.division_name;
  if (clause && typeof clause === "object" && "value" in clause) {
    const v = (clause as { value?: unknown }).value;
    if (Array.isArray(v)) return v.map(String);
    if (typeof v === "string") return [v];
  }
  return [];
}

export default function ArAgingWidget({ widget }: ArAgingWidgetProps) {
  const state = useArAgingSnapshot();
  const rule = useMemo(
    () => normalizeSegmentRule(widget.ar_aging?.segment_rule ?? null),
    [widget.ar_aging?.segment_rule],
  );
  const divisions = selectedDivisions(widget);
  const divisionKey = divisions.join(",");
  const rows = state.kind === "ready" ? state.snapshot.rows : null;
  const data = useMemo(() => {
    if (!rows) return null;
    const { snapshotDate, accounts } = accountsFromRows(
      rows,
      rule,
      divisionKey ? divisionKey.split(",") : [],
    );
    return { snapshotDate, accounts, summary: summarize(accounts, rule) };
  }, [rows, rule, divisionKey]);

  if (state.kind === "loading") return <Skeleton label={widget.name} />;
  if (state.kind === "missing") return <EmptyState title={widget.name} caption={GATE_COPY} />;
  if (state.kind === "error") return <ErrorState message={state.message} />;
  if (!data || data.accounts.length === 0) {
    return <EmptyState title={widget.name} caption={GATE_COPY} />;
  }

  const { accounts, summary, snapshotDate } = data;
  const view = widget.ar_aging?.view;
  let body: ReactNode;
  switch (view) {
    case "bucket_tiles":
      body = <BucketTiles accounts={accounts} summary={summary} rule={rule} />;
      break;
    case "segment_panels":
      body = rule ? (
        <SegmentPanels summary={summary} rule={rule} />
      ) : (
        <EmptyState title={widget.name} caption={GATE_COPY} />
      );
      break;
    case "distribution":
      body = <Distribution summary={summary} />;
      break;
    case "status_counts":
      body = <StatusCounts summary={summary} />;
      break;
    case "risk_top":
      body = <RiskTop accounts={accounts} />;
      break;
    case "warning":
      body = <Warning accounts={accounts} />;
      break;
    case "critical":
      body = <Critical accounts={accounts} rule={rule} />;
      break;
    case "bucket_pages":
      body = <BucketPages accounts={accounts} rule={rule} />;
      break;
    case "all_accounts":
      body = <AllAccounts accounts={accounts} rule={rule} />;
      break;
    default:
      return <ErrorState message={`Unknown AR Aging view for widget ${widget.id}`} />;
  }
  return (
    <div data-ar-aging-view={view}>
      <div style={{ ...small, marginBottom: 8 }}>
        AR as of {snapshotDate} · {summary.accountCount.toLocaleString()} accounts
        {divisions.length > 0 ? ` · ${divisions.join(", ")}` : ""}
        {state.kind === "ready" && state.snapshot.truncated
          ? " · list truncated, not every account was read"
          : ""}
      </div>
      {body}
    </div>
  );
}
